import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../api';
import { PageShell } from '../components/PageShell';
import type { AlertaReconc } from '../types';

/**
 * Reconciliação (gap-detector) — lista os alertas de POSSÍVEL prazo não-capturado
 * (movimento relevante no DataJud sem publicação DJEN correspondente). A Dra. Maria
 * revisa cada um: confere no PJe e marca "Revisado" (tratei) ou "Ignorar" (não era
 * nosso / sem prazo). É a rede de segurança "não falta prazo".
 */
export function Reconciliacao() {
  const qc = useQueryClient();
  const [status, setStatus] = useState<'aberto' | 'revisado' | 'ignorado'>('aberto');

  const { data: alertas, isLoading } = useQuery({
    queryKey: ['reconciliacao', status],
    queryFn: () => api.reconciliacaoAlertas(status),
  });
  const { data: resumo } = useQuery({
    queryKey: ['reconciliacao-resumo'],
    queryFn: () => api.reconciliacaoResumo(),
  });

  const resolver = useMutation({
    mutationFn: ({ chave, novo }: { chave: string; novo: string }) =>
      api.reconciliacaoResolver(chave, novo),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['reconciliacao'] });
      qc.invalidateQueries({ queryKey: ['reconciliacao-resumo'] });
    },
  });

  const lista = alertas ?? [];

  return (
    <PageShell titulo={<h1 className="text-lg font-semibold text-slate-800 dark:text-slate-100">🛡 Reconciliação de prazos</h1>}>
      <div className="space-y-4">
        <p className="text-sm text-slate-500 dark:text-slate-400">
          Cruzamento independente <strong>DataJud × DJEN</strong>: movimentos do processo
          (sentença, acórdão, decisão, embargos, execução…) que <strong>não</strong> têm
          uma intimação capturada no nosso sistema. Confira cada um no PJe.
        </p>

        {resumo && (
          <div className="flex flex-wrap gap-3 text-sm">
            <span className="rounded-lg bg-rose-50 dark:bg-rose-950/40 text-rose-700 dark:text-rose-300 px-3 py-1.5 font-medium">
              {resumo.abertos} alertas abertos · {resumo.processos} processos
            </span>
            {Object.entries(resumo.por_movimento || {}).map(([nome, n]) => (
              <span key={nome} className="rounded-lg bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 px-3 py-1.5">
                {nome}: <strong>{n}</strong>
              </span>
            ))}
          </div>
        )}

        <div className="flex gap-2">
          {(['aberto', 'revisado', 'ignorado'] as const).map((s) => (
            <button
              key={s}
              onClick={() => setStatus(s)}
              className={`px-3 py-1 rounded-lg text-sm capitalize transition ${
                status === s
                  ? 'bg-blue-600 text-white'
                  : 'bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-700'
              }`}
            >
              {s}
            </button>
          ))}
        </div>

        {isLoading ? (
          <p className="text-sm text-slate-400">Carregando…</p>
        ) : lista.length === 0 ? (
          <p className="text-sm text-slate-400 py-6 text-center">Nenhum alerta {status}. 🎉</p>
        ) : (
          <ul className="space-y-2">
            {lista.map((a: AlertaReconc) => (
              <li
                key={a.chave}
                className="flex items-center justify-between gap-3 rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900/40 p-3"
              >
                <div className="min-w-0">
                  <div className="flex items-center gap-2 text-sm">
                    <span className="font-mono text-slate-700 dark:text-slate-200">{a.numero_processo}</span>
                    <span className="text-[11px] font-mono text-slate-400">{a.data_movimento}</span>
                  </div>
                  <div className="text-[13px] font-medium text-rose-700 dark:text-rose-300 mt-0.5">
                    {a.nome_movimento}
                  </div>
                  {a.detalhe && (
                    <div className="text-[11px] text-slate-400 mt-0.5 truncate">{a.detalhe}</div>
                  )}
                </div>
                {status === 'aberto' && (
                  <div className="flex gap-1.5 shrink-0">
                    <a
                      href={`https://pje.trt1.jus.br/consultaprocessual/detalhe-processo/${a.numero_processo}`}
                      target="_blank" rel="noreferrer"
                      className="px-2.5 py-1 text-xs rounded-lg bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 hover:bg-slate-200"
                    >
                      🔎 Ver no PJe
                    </a>
                    <button
                      onClick={() => resolver.mutate({ chave: a.chave, novo: 'revisado' })}
                      disabled={resolver.isPending}
                      className="px-2.5 py-1 text-xs rounded-lg bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50"
                    >
                      ✓ Revisado
                    </button>
                    <button
                      onClick={() => resolver.mutate({ chave: a.chave, novo: 'ignorado' })}
                      disabled={resolver.isPending}
                      className="px-2.5 py-1 text-xs rounded-lg bg-slate-200 dark:bg-slate-700 text-slate-600 dark:text-slate-200 hover:bg-slate-300"
                    >
                      Ignorar
                    </button>
                  </div>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>
    </PageShell>
  );
}
